/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { WatchFromVodActionKernel } from 'client-code/action-details';
import { AiringOffer } from 'client-code/airing';
import { StationPolicy } from 'client-code/channels-service';
import { Carousel, Carousels, FeedItemType, OfferType } from 'client-code/wrs-feed-service';

import { DEFAULT_RETRY_OPTIONS, TIVO_SERVICES } from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { DeviceFeatures } from '../../core/device';
import { DeviceModel } from '../../core/device/DeviceModel';
import { ErrorTypes } from '../../core/error/ErrorTypes';
import { WrsFeedService } from '../../core/infrastructure/WrsFeedService';
import { HomeScreenInteractor } from '../../core/interactors/HomeScreenInteractor';
import { QuickMenuShortcutInteractor } from '../../core/interactors/QuickMenuShortcutInteractor';
import { ServicesClientImpl } from '../../core/interactors/ServicesClientImpl';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { Log } from '../../core/logging/Log';
import { KeyManager } from '../../core/navigation/KeyManager';
import { NetworkManager } from '../../core/network/NetworkManager';
import { PlaybackContentType } from '../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import { RetryMechanism } from '../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../core/retrymechanism/RetryOptions';
import { Storage } from '../../core/storage';
import type { TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import type { TChannelV2 } from '../../core/types';
import { VodPlayAction } from '../../core/usecases/VodPlayAction';
import { NdvrPlaybackType } from '../../core/videoplayer/enums';
import { IPlayable, IVodPlaybackModel } from '../../core/videoplayer/interfaces';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import { startVideoPlayback } from '../videoplayer/videoplayer.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    loadHomeScreenDataAction,
    loadMoreCarouselsAction,
    refreshHomeScreenAction,
    setHomeScreenErrorAction,
    setHomeScreenFocusAction,
    setHomeScreenLoadingAction,
    setHomeScreenLoadingMoreCarouselsAction,
    setHomeScreenNavFocusAction,
    setHomeScreenNavItemsAction,
    setHomeScreenPreviewAction,
} from './homescreen.actions';
import { THomeScreenState, TNavItem } from './homescreen.props';
import {
    LOAD_HOME_SCREEN_DATA,
    LOAD_HOME_SCREEN_MORE_CAROUSELS,
    LOAD_HOME_SCREEN_MORE_FEED_ITEMS,
    REFRESH_HOME_SCREEN,
    RESET_HOME_SCREEN,
    SET_HOME_SCREEN_ERROR,
    SET_HOME_SCREEN_FOCUS,
    SET_HOME_SCREEN_GHOST,
    SET_HOME_SCREEN_LOADING,
    SET_HOME_SCREEN_LOADING_MORE_CAROUSELS,
    SET_HOME_SCREEN_NAV_FOCUS,
    SET_HOME_SCREEN_NAV_ITEMS,
    SET_HOME_SCREEN_PREVIEW,
    SET_HOME_SCREEN_VERTICAL_SCROLL,
} from './homescreen.types';

const TAG = 'homeScreenReducer';

const HOME_SCREEN_NAME = 'kaveriHome';
const INITIAL_FEED_ITEM_COUNT = 10;
const CAROUSEL_PAGE_COUNT = 4;
const TILE_PREFETCH_THRESHOLD = 4;

const SPORTS_ON_NOW_CAPTION = 'Sports On Now';
const SPORTS_ON_LATER_CAPTION = 'Sports On Later';
const PREVIEW_DEBOUNCE_MS = 300;

/** Destination-to-icon-class mapping for nav bar items from the QuickMenu Shortcut API. */
const DESTINATION_ICON_MAP: Record<string, string> = {
    guide: 'guide',
    myShows: 'myshows',
    vodBrowse: 'ondemand',
};

export const DEFAULT_NAV_ITEMS: TNavItem[] = [];

let previewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let previewNetworkCallId = 0;
let isFetchingMoreFeedItems = false;
let isFetchingMoreCarousels = false;

const dispatchHomeScreenErrorWhisper = (dispatch: Function, errorCode: string) => {
    const whisperMessage = Locale.getString(LocaleKeys.HOME_SCREEN_ERROR_MESSAGE).replace(
        '$ERROR_CODE',
        errorCode,
    );
    dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.INFO }));
};

const initialState: THomeScreenState = {
    carousels: [],
    pageCursor: '',
    refreshIntervalSecs: 120,
    focusState: {
        carouselIndex: 0,
        tileIndex: 0,
    },
    tileIndexPerCarousel: {},
    verticalScrollIndex: 0,
    navBarFocused: false,
    navBarIndex: 0,
    navItems: DEFAULT_NAV_ITEMS,
    ghostState: {
        carouselIndex: null,
        tileIndex: null,
        navBarIndex: null,
        scrollOffset: 0,
    },
    previewData: null,
    isLoading: false,
    isLoadingMoreCarousels: false,
    isError: false,
    lastFetchTimestamp: 0,
};

const homeScreenReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<THomeScreenState>>{},
): THomeScreenState => {
    switch (type) {
        case LOAD_HOME_SCREEN_DATA:
            return {
                ...state,
                carousels: payload.carousels ?? state.carousels,
                pageCursor: payload.pageCursor ?? '',
                refreshIntervalSecs: payload.refreshIntervalSecs ?? state.refreshIntervalSecs,
                isLoading: false,
                isError: false,
                lastFetchTimestamp: payload.lastFetchTimestamp ?? Date.now(),
            };

        case LOAD_HOME_SCREEN_MORE_CAROUSELS:
            return {
                ...state,
                carousels: [...state.carousels, ...(payload.carousels ?? [])],
                pageCursor: payload.pageCursor ?? '',
                isLoadingMoreCarousels: false,
            };

        case LOAD_HOME_SCREEN_MORE_FEED_ITEMS: {
            const updatedCarousel = (payload as any).carousels?.[0] as Carousel | undefined;
            if (!updatedCarousel) return state;
            const targetIndex = state.carousels.findIndex(
                (c) => c.carouselName === updatedCarousel.carouselName,
            );
            if (targetIndex === -1) return state;
            const newCarousels = [...state.carousels];
            const existing = newCarousels[targetIndex];
            newCarousels[targetIndex] = {
                ...existing,
                feedItems: {
                    ...existing.feedItems,
                    resolvedCount:
                        updatedCarousel.feedItems?.resolvedCount ??
                        existing.feedItems?.resolvedCount,
                    feedItems: [
                        ...(existing.feedItems?.feedItems ?? []),
                        ...(updatedCarousel.feedItems?.feedItems ?? []),
                    ],
                },
            };
            return {
                ...state,
                carousels: newCarousels,
            };
        }

        case SET_HOME_SCREEN_FOCUS: {
            const newFocus = payload.focusState ?? state.focusState;
            return {
                ...state,
                focusState: newFocus,
                tileIndexPerCarousel: {
                    ...state.tileIndexPerCarousel,
                    [state.focusState.carouselIndex]: state.focusState.tileIndex,
                },
            };
        }

        case SET_HOME_SCREEN_PREVIEW:
            return {
                ...state,
                previewData: payload.previewData ?? null,
            };

        case SET_HOME_SCREEN_ERROR:
            return {
                ...state,
                isError: payload.isError ?? false,
                isLoading: false,
            };

        case SET_HOME_SCREEN_LOADING:
            return {
                ...state,
                isLoading: payload.isLoading ?? false,
            };

        case SET_HOME_SCREEN_NAV_FOCUS:
            return {
                ...state,
                navBarFocused: payload.navBarFocused ?? false,
                navBarIndex: payload.navBarIndex ?? 0,
            };

        case SET_HOME_SCREEN_GHOST:
            return {
                ...state,
                ghostState: payload.ghostState ?? initialState.ghostState,
            };

        case SET_HOME_SCREEN_LOADING_MORE_CAROUSELS:
            return {
                ...state,
                isLoadingMoreCarousels: payload.isLoadingMoreCarousels ?? false,
            };

        case SET_HOME_SCREEN_VERTICAL_SCROLL:
            return {
                ...state,
                verticalScrollIndex: payload.verticalScrollIndex ?? state.verticalScrollIndex,
            };

        case SET_HOME_SCREEN_NAV_ITEMS:
            return {
                ...state,
                navItems: payload.navItems ?? state.navItems,
            };

        case RESET_HOME_SCREEN:
            return {
                ...initialState,
                navBarFocused: state.navBarFocused,
                navBarIndex: state.navBarIndex,
                navItems: state.navItems,
                focusState: state.focusState,
                verticalScrollIndex: state.verticalScrollIndex,
                isLoadingMoreCarousels: false,
            };

        case REFRESH_HOME_SCREEN:
            return {
                ...initialState,
                navBarFocused: state.navBarFocused,
                navBarIndex: state.navBarIndex,
                navItems: state.navItems,
            };

        default:
            return state;
    }
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * The server sends both "Sports On Now" and "Sports On Later" carousels.
 * Client-side rule: if "Sports On Now" has items, exclude "Sports On Later".
 * When "Sports On Now" is absent or empty, "Sports On Later" remains visible.
 */
const applySportsOnLaterRule = (carousels: Carousel[]): Carousel[] => {
    const hasSportsOnNow = carousels.some(
        (c) =>
            c.caption?.toLowerCase() === SPORTS_ON_NOW_CAPTION.toLowerCase() &&
            c.feedItems?.feedItems?.length > 0,
    );
    if (!hasSportsOnNow) return carousels;
    return carousels.filter(
        (c) => c.caption?.toLowerCase() !== SPORTS_ON_LATER_CAPTION.toLowerCase(),
    );
};

const filterAndDispatchCarousels = (
    dispatch: Function,
    getState: TGetState,
    response: { carousels?: Carousel[]; pageCursor?: string; refreshIntervalSecs?: number },
): Carousel[] => {
    const carousels = response.carousels ?? [];
    const nonEmpty = carousels.filter((c) => c.feedItems?.feedItems?.length > 0);
    const visibleCarousels = applySportsOnLaterRule(nonEmpty);
    dispatchCarouselsAndValidateFocus(
        dispatch,
        getState,
        visibleCarousels,
        response.pageCursor ?? '',
        response.refreshIntervalSecs ?? initialState.refreshIntervalSecs,
    );
    return visibleCarousels;
};

const dispatchCarouselsAndValidateFocus = (
    dispatch: Function,
    getState: TGetState,
    visibleCarousels: Carousel[],
    pageCursor: string,
    refreshIntervalSecs: number,
) => {
    dispatch(loadHomeScreenDataAction(visibleCarousels, pageCursor, refreshIntervalSecs));

    const { focusState } = getState().homeScreen;
    if (focusState.carouselIndex >= visibleCarousels.length) {
        if (pageCursor) {
            dispatch(
                setHomeScreenFocusAction({
                    carouselIndex: focusState.carouselIndex,
                    tileIndex: 0,
                }),
            );
        } else {
            dispatch(setHomeScreenFocusAction({ carouselIndex: 0, tileIndex: 0 }));
        }
    } else {
        dispatch(
            setHomeScreenFocusAction({
                carouselIndex: focusState.carouselIndex,
                tileIndex: 0,
            }),
        );
    }
};

export const getSingleChannelInfo = (stationId: string): TChannelV2 | null => {
    const channels: TChannelV2[] = Storage.getInstance().getItem(Storage.Keys.CHANNELS_V2) ?? [];
    const channel = channels.find((ch) => ch.stationId === stationId && ch.isSubscribed) ?? null;
    return resolveChannelPolicyInfo(channel);
};

const resolveChannelPolicyInfo = (channel: TChannelV2 | null): TChannelV2 | null => {
    if (channel && !channel.policyInfo && channel.policyName?.length) {
        const policies: StationPolicy[] =
            Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES) ?? [];
        const policyMap = new Map(policies.map((p) => [p.name, p]));
        channel.policyInfo = channel.policyName
            .filter((name) => policyMap.has(name))
            .map((name) => policyMap.get(name)!);
    }
    return channel;
};

const FALLBACK_PREVIEW = { contentPreview: null };

const normalizeAiringToPreview = (airing: AiringOffer, feedItemType?: string): object => {
    const { banner, startTime, duration, stationId, logging } = airing;
    const endTime = duration
        ? AppUtils.addSecondsToDateTime(new Date(startTime), duration)?.toISOString()
        : undefined;
    const channelInfo = getSingleChannelInfo(stationId);

    return {
        offerType: OfferType.Live,
        feedItemType,
        contentPreview: {
            title: banner?.title ?? '',
            episodeTitle: banner?.subtitle ?? '',
            seasonNumber: banner?.seasonNumber,
            episodeNumber: banner?.episodeNumber,
            movieYear: banner?.movieYear,
            categoryLabel: logging?.genre ?? '',
            startTime,
            endTime,
            duration,
            channel: {
                callSign: channelInfo?.callSign ?? '',
                channelNumber: channelInfo?.channelNumber ?? '',
                affiliate: channelInfo?.affiliate ?? '',
            },
            internalRatingList: airing.parentalControls?.rating ?? [],
        },
    };
};

// ─── Thunks ─────────────────────────────────────────────────────────────────

const getWrsFeedService = (): WrsFeedService => {
    return new WrsFeedService({
        accessToken: DeviceModel.getAccessToken(),
        basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.WrsFeedService],
        bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
        whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
    });
};

export const fetchHomeScreenNavItems = () => async (dispatch: Function) => {
    try {
        const quickMenuService = new QuickMenuShortcutInteractor();
        const shortcuts = await quickMenuService.fetchQuickMenuShortcutsWithFallback();

        const navItems: TNavItem[] = shortcuts
            .filter((shortcut) => {
                const dest = (shortcut.uiAction as { destination?: string }).destination;
                // Settings is rendered separately — exclude it from nav items
                return dest && dest !== 'settings';
            })
            .map((shortcut) => {
                const { destination } = shortcut.uiAction as { destination: string };
                const iconClass = DESTINATION_ICON_MAP[destination] ?? '';
                return {
                    label: shortcut.title,
                    key: destination,
                    iconClass,
                    destination,
                };
            });

        if (navItems.length > 0) {
            dispatch(setHomeScreenNavItemsAction(navItems));
        }
    } catch (error) {
        Log.e(
            TAG,
            'Failed to fetch nav items from QuickMenu Shortcut API — using defaults.',
            error,
        );
    }
};

export const loadHomeScreenData = () => async (dispatch: Function, getState: TGetState) => {
    dispatch(setHomeScreenLoadingAction(true));

    if (NetworkManager.isNetworkDisconnected()) {
        Log.w(TAG, 'Network disconnected — cannot load home screen data.');
        dispatch(setHomeScreenErrorAction(true));
        dispatchHomeScreenErrorWhisper(
            dispatch,
            ErrorTypes.HOME_SCREEN_WRS_FEED_SCREEN_ERROR_C2501,
        );
        return;
    }

    try {
        const service = getWrsFeedService();
        const fetchScreen = service.fetchScreen.bind(service);
        const response = await RetryMechanism.callServiceWithRetry<Carousels>(
            fetchScreen,
            [
                HOME_SCREEN_NAME,
                undefined,
                CAROUSEL_PAGE_COUNT,
                INITIAL_FEED_ITEM_COUNT,
                undefined,
                true,
            ],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        );

        const visibleCarousels = filterAndDispatchCarousels(dispatch, getState, response);

        Log.i(
            TAG,
            `Home screen loaded: ${visibleCarousels.length} carousels, pageCursor=${response.pageCursor ?? 'none'}`,
        );
    } catch (error) {
        Log.e(TAG, 'Failed to load home screen data.', error);
        dispatch(setHomeScreenErrorAction(true));
        dispatch(setHomeScreenNavFocusAction(true, 0));
        dispatchHomeScreenErrorWhisper(
            dispatch,
            ErrorTypes.HOME_SCREEN_WRS_FEED_SCREEN_ERROR_C2501,
        );
    }
};

export const refreshHomeScreenData =
    (isProgramBoundaryRefresh = false) =>
    async (dispatch: Function, getState: TGetState) => {
        const { homeScreen } = getState();
        const elapsed = Date.now() - homeScreen.lastFetchTimestamp;
        const ttl = homeScreen.refreshIntervalSecs * 1000;

        if (!isProgramBoundaryRefresh && (homeScreen.isLoading || elapsed < ttl)) {
            Log.i(TAG, 'Home screen data is still fresh or loading — skipping refresh.');
            return;
        }

        dispatch(refreshHomeScreenAction());
        dispatch(loadHomeScreenData());
    };

export const loadMoreCarousels = () => async (dispatch: Function, getState: TGetState) => {
    if (isFetchingMoreCarousels) return;

    const { homeScreen } = getState();

    if (!homeScreen.pageCursor) {
        Log.i(TAG, 'No more carousel pages to load.');
        return;
    }

    isFetchingMoreCarousels = true;
    dispatch(setHomeScreenLoadingMoreCarouselsAction(true));
    try {
        const service = getWrsFeedService();
        const fetchScreen = service.fetchScreen.bind(service);
        const response = await RetryMechanism.callServiceWithRetry<Carousels>(
            fetchScreen,
            [
                HOME_SCREEN_NAME,
                undefined,
                CAROUSEL_PAGE_COUNT,
                INITIAL_FEED_ITEM_COUNT,
                undefined,
                true,
                undefined,
                homeScreen.pageCursor,
            ],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        );

        const carousels = response.carousels ?? [];
        const nonEmpty = carousels.filter((c) => c.feedItems?.feedItems?.length > 0);

        // Also apply the sports rule against the full carousel set (existing + new)
        const existingCarousels = getState().homeScreen.carousels;
        const allCarousels = [...existingCarousels, ...nonEmpty];
        const filteredAll = applySportsOnLaterRule(allCarousels);
        // Keep only the newly loaded carousels that survived the filter
        const visibleCarousels = nonEmpty.filter((c) => filteredAll.includes(c));

        // isLoadingMoreCarousels is cleared by the LOAD_HOME_SCREEN_MORE_CAROUSELS reducer case
        dispatch(loadMoreCarouselsAction(visibleCarousels, response.pageCursor ?? ''));
    } catch (error) {
        Log.w(TAG, 'Failed to load more carousels.', error);
        dispatch(setHomeScreenLoadingMoreCarouselsAction(false));
        dispatchHomeScreenErrorWhisper(
            dispatch,
            ErrorTypes.HOME_SCREEN_WRS_FEED_SCREEN_ERROR_C2501,
        );
    } finally {
        isFetchingMoreCarousels = false;
    }
};

export const loadMoreFeedItems =
    (carouselIndex: number) => async (dispatch: Function, getState: TGetState) => {
        if (isFetchingMoreFeedItems) return;

        const { homeScreen } = getState();
        const carousel = homeScreen.carousels[carouselIndex];

        if (!carousel) return;

        const loadedCount = carousel.feedItems?.feedItems?.length ?? 0;
        const resolvedCount = carousel.feedItems?.resolvedCount ?? 0;

        if (loadedCount >= resolvedCount) {
            Log.i(TAG, `All feed items loaded for carousel: ${carousel.carouselName}`);
            return;
        }

        isFetchingMoreFeedItems = true;
        try {
            const service = getWrsFeedService();
            const fetchCarousel = service.fetchCarousel.bind(service);
            const response = await RetryMechanism.callServiceWithRetry<Carousel>(
                fetchCarousel,
                [carousel.carouselName, loadedCount, INITIAL_FEED_ITEM_COUNT, undefined, true],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            );

            dispatch({
                type: LOAD_HOME_SCREEN_MORE_FEED_ITEMS,
                payload: {
                    carousels: [response],
                },
            });
        } catch (error) {
            Log.w(
                TAG,
                `Failed to load more feed items for carousel: ${carousel.carouselName}`,
                error,
            );
            dispatchHomeScreenErrorWhisper(
                dispatch,
                ErrorTypes.HOME_SCREEN_WRS_FEED_CAROUSEL_ERROR_C2502,
            );
        } finally {
            isFetchingMoreFeedItems = false;
        }
    };

// ─── Preview Fetch Helpers ──────────────────────────────────────────────────

const fetchSeriesPreview = async (feedItem: any): Promise<object | null> => {
    if (!feedItem.feedItemId) return null;

    const interactor = new HomeScreenInteractor();
    const ucResponse = await interactor.getUnifiedCollectionPreview(feedItem.feedItemId);
    if (!ucResponse) return null;

    return {
        ...ucResponse,
        offerType: feedItem.offerType,
        feedItemType: feedItem.feedItemType,
        contentPreview: {
            internalRatingList: ucResponse.internalRatingList,
            title: ucResponse.title,
            categoryLabel: ucResponse.categoryLabel,
        },
    };
};

const fetchLivePreview = async (feedItem: any): Promise<object | null> => {
    if (!feedItem.stationId) return null;

    const service = new ServicesClientImpl();
    const airingResponse = await service.fetchAiringOffers(feedItem.stationId);
    const currentAiring = airingResponse?.airingOffers?.[0];
    if (!currentAiring) return null;

    return normalizeAiringToPreview(currentAiring, feedItem.feedItemType);
};

const fetchVodPreview = async (feedItem: any): Promise<object | null> => {
    if (!feedItem.offerId) return null;

    const interactor = new HomeScreenInteractor();
    const response = await interactor.getVodOfferPreview(feedItem.offerId);
    if (!response?.vodOfferPreview) return null;

    return {
        ...response.vodOfferPreview,
        offerType: feedItem.offerType,
        feedItemType: feedItem.feedItemType,
    };
};

const fetchOfferPreview = async (feedItem: any): Promise<object | null> => {
    if (!feedItem.offerId) return null;
    const interactor = new HomeScreenInteractor();
    const response = await interactor.getOfferPreview(feedItem.offerId);
    const offerPreview = response?.offerPreview;
    if (!offerPreview) return null;

    return {
        ...offerPreview,
        offerType: feedItem.offerType,
        feedItemType: feedItem.feedItemType,
    };
};

const fetchRecordingPreview = async (feedItem: any): Promise<object | null> => {
    if (!feedItem.feedItemId) return null;

    const interactor = new HomeScreenInteractor();
    const response = await interactor.getRecordingOfferPreview(feedItem.feedItemId);
    const recPreview = response?.offerPreview;
    if (!recPreview) return null;

    const contentPreview: any = recPreview.contentPreview ?? {};
    if (!contentPreview.channel && feedItem.stationId) {
        contentPreview.channel = getSingleChannelInfo(feedItem.stationId);
    }

    return {
        ...recPreview,
        offerType: feedItem.offerType,
        feedItemType: feedItem.feedItemType,
        contentPreview,
    };
};

const fetchPreviewByType = async (feedItem: any): Promise<object | null> => {
    // Sports on Later is future content which fetches for offerPreview
    const isFutureContent = feedItem.startTime && Date.parse(feedItem.startTime) > Date.now();
    if (isFutureContent) {
        return fetchOfferPreview(feedItem);
    }
    // Series tiles use Unified Collection Preview API (independent of offerType)
    if (feedItem.feedItemType === FeedItemType.Series) {
        return fetchSeriesPreview(feedItem);
    }

    // Non-Series content: route by offerType
    switch (feedItem.offerType) {
        case OfferType.Live:
            return fetchLivePreview(feedItem);
        case OfferType.Vod:
        case OfferType.Socu:
            return fetchVodPreview(feedItem);
        case OfferType.Recording:
            return fetchRecordingPreview(feedItem);
        default:
            Log.w(TAG, `Unsupported offer type for preview: ${feedItem.offerType}`);
            return null;
    }
};

export const fetchPreviewForFocusedTile = () => async (dispatch: Function, getState: TGetState) => {
    if (previewDebounceTimer) {
        clearTimeout(previewDebounceTimer);
        previewDebounceTimer = null;
    }

    dispatch(setHomeScreenPreviewAction(null));

    previewDebounceTimer = setTimeout(async () => {
        const { homeScreen } = getState();
        const { focusState, carousels } = homeScreen;
        const carousel = carousels[focusState.carouselIndex];
        const feedItem = carousel?.feedItems?.feedItems?.[focusState.tileIndex];

        if (!feedItem) return;

        const callId = ++previewNetworkCallId;

        try {
            const previewData = await fetchPreviewByType(feedItem);

            // Stale response guard
            if (callId !== previewNetworkCallId) return;

            dispatch(setHomeScreenPreviewAction(previewData ?? FALLBACK_PREVIEW));
        } catch (error) {
            Log.w(TAG, 'Failed to fetch preview.', error);
            if (callId === previewNetworkCallId) {
                dispatch(setHomeScreenPreviewAction(FALLBACK_PREVIEW));
            }
        }
    }, PREVIEW_DEBOUNCE_MS);
};

export const checkAndLoadMoreFeedItems = () => (dispatch: Function, getState: TGetState) => {
    const { homeScreen } = getState();
    const { focusState, carousels } = homeScreen;
    const carousel = carousels[focusState.carouselIndex];

    if (!carousel) return;

    const loadedCount = carousel.feedItems?.feedItems?.length ?? 0;
    const resolvedCount = carousel.feedItems?.resolvedCount ?? 0;

    if (
        focusState.tileIndex >= loadedCount - TILE_PREFETCH_THRESHOLD &&
        loadedCount < resolvedCount
    ) {
        dispatch(loadMoreFeedItems(focusState.carouselIndex));
    }
};

export const checkAndLoadMoreCarousels = () => (dispatch: Function, getState: TGetState) => {
    const { homeScreen } = getState();
    const { focusState, carousels } = homeScreen;

    if (focusState.carouselIndex >= carousels.length - 1 && homeScreen.pageCursor) {
        dispatch(loadMoreCarousels());
    }
};

/**
 * Start VOD/Socu playback directly from the home screen.
 */
export const startVodPlayback = (feedItem: any) => async (dispatch: Function) => {
    try {
        const actionDetails = await new VodPlayAction().getVodOttActionDetails({
            itemType: 'offer',
            id: feedItem.offerId ?? '',
            partnerId: feedItem.partnerId ?? '',
        });

        const watchActionsList = actionDetails?.actionDetails?.watchActionsList;
        if (!watchActionsList || watchActionsList.length === 0) {
            Log.w(TAG, 'No watch actions available for VOD/Socu playback');
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }

        const selectedAction = watchActionsList[0];
        const kernel = selectedAction.kernel as WatchFromVodActionKernel;
        const { offerId, savedPosition, duration, contentSupplierPartnerId } = kernel;

        // Resume from saved position if valid; otherwise start from beginning
        const hasSavedPosition = !!savedPosition && !(duration && savedPosition >= duration);
        const isStartOver = !hasSavedPosition;

        // Store action details so VodMetadataModelImpl can resolve watchVodAction
        // (required for VOD_ACTION_LIST and session setup during playback)
        const playbackDataModel = PlaybackDataModelImpl.getInstance();
        playbackDataModel.setActionDetails(selectedAction);
        playbackDataModel.setActionDetailsList(actionDetails);
        playbackDataModel.setPlaybackData(PlaybackContentType.VOD);

        const vodPlaybackModel: IVodPlaybackModel = {
            id: offerId,
            isStartOver,
            videoProviderId: feedItem.partnerId,
            contentSupplierPartnerId,
        };

        const playable: IPlayable = {
            playbackContentType: PlaybackContentType.VOD,
            vodPlaybackModel,
        };

        dispatch(startVideoPlayback(playable));
    } catch (error) {
        Log.e(TAG, 'Failed to start VOD playback from homescreen:', error);
        KeyManager.getInstance().stopKeyIgnoring();
    }
};

/**
 * Start NDVR recording playback from the home screen.
 */
export const startNdvrPlayback = (recordingId: string) => (dispatch: Function) => {
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    playbackModelImpl.setPlaybackData(PlaybackContentType.NDVR_RECORDED);
    const playable: IPlayable = {
        playbackContentType: PlaybackContentType.NDVR_RECORDED,
        ndvrPlaybackModel: {
            id: recordingId,
            ndvrType: NdvrPlaybackType.RECORDING,
        },
    };
    dispatch(startVideoPlayback(playable));
};

export default homeScreenReducer;
